import React, { FC, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PollEllipsisMenuItem } from './poll-ellipsis-menu-item';
import {
  EllipsisMenu,
  LEFT_COORDINATS_SUM_PX,
  MOBILE_ELIPSIS_LEFT_CORRECTION,
  MOBILE_ELIPSIS_TOP_CORRECTION,
  MOBILE_ELIPSIS_TOP_CORRECTION_MOBILE,
  PollsTabsOnMobile,
  PollsViewStatuses
} from '../../../constans/polls-constants';
import pencilIcon from '../../../assets/icons/pencil.svg';
import closeIcon from '../../../assets/icons/close-icon.svg';
import pollIcon from '../../../assets/icons/polls-bar.svg';
import circleArrowIcon from '../../../assets/icons/circle-arrow.svg';
import './poll-ellipsis-menu.scss';
import { getMixpanelService } from '../../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../../constans/mixpanel-contants';

interface IProps {
  setShouldShowEllipsisMenu: (value: boolean) => void;
  setShouldShowDeleteModal: (value: boolean) => void;
  setShouldShowResetModal: (value: boolean) => void;
  reopenPollHandler: () => void;
  toggleManagePollModal: () => void;
  forwardedRef: RefObject<HTMLButtonElement>;
  pollStatus: PollsViewStatuses;
  setActiveMobileTab: (tab: PollsTabsOnMobile) => void;
  isMobile: boolean;
  isOnePollOpened: boolean;
  isResultsSharingLockedByOtherPoll?: boolean;
  isActivePollSharingResults?: boolean;
}

const getClientRectCoordinates = (current: HTMLButtonElement | null, isMobile: boolean): Pick<DOMRect, 'left' | 'top'> => {
  const NEGATIVE = -1;
  const clientRect = current?.getBoundingClientRect();
  const topCorrection = isMobile ? MOBILE_ELIPSIS_TOP_CORRECTION_MOBILE * NEGATIVE : MOBILE_ELIPSIS_TOP_CORRECTION;
  const left = isMobile && clientRect ? clientRect?.left - MOBILE_ELIPSIS_LEFT_CORRECTION : clientRect?.left;
  const top = isMobile && clientRect ? clientRect?.top + topCorrection : clientRect?.top;

  return {
    left: left || 0,
    top: top || 0
  };
};

const PollEllipsisMenu: FC<IProps> = ({
  setShouldShowEllipsisMenu,
  toggleManagePollModal,
  reopenPollHandler,
  forwardedRef,
  setShouldShowDeleteModal,
  setShouldShowResetModal,
  setActiveMobileTab,
  pollStatus,
  isMobile,
  isOnePollOpened,
  isResultsSharingLockedByOtherPoll,
  isActivePollSharingResults
}) => {
  const initialCoordinates = getClientRectCoordinates(forwardedRef.current, isMobile);
  const [leftCoordinates, setLeftCoordinates] = useState<number>(initialCoordinates.left + LEFT_COORDINATS_SUM_PX);
  const [topCoordinates, setTopCoordinates] = useState<number>(initialCoordinates.top);
  const menuRef = useRef<HTMLDivElement>(null);

  const editActionHandler = () => {
    toggleManagePollModal();
    setShouldShowEllipsisMenu(false);
    setActiveMobileTab(PollsTabsOnMobile.DETAILS);
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.EDITED);
  };

  const deleteActionHandler = (): void => {
    setShouldShowDeleteModal(true);
  };

  const resetActionHandler = (): void => {
    setShouldShowResetModal(true);
  };

  const reopenPollActionHandler = (): void => {
    reopenPollHandler();
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.REOPENED);
  };

  // The menu is fixed-positioned at the three dots' viewport Y, so for a poll at the bottom of the list it
  // would run past the viewport edge and its last item (Delete) could never be reached — scrolling does not
  // move a fixed element. Flip it above the three dots whenever it does not fit below them.
  const setCoordinates = (): void => {
    const cooridnates = getClientRectCoordinates(forwardedRef.current, isMobile);
    const menuHeight = menuRef.current?.offsetHeight || 0;
    const doesFitBelow = cooridnates.top + menuHeight <= window.innerHeight;

    setLeftCoordinates(cooridnates.left + LEFT_COORDINATS_SUM_PX);
    setTopCoordinates(doesFitBelow ? cooridnates.top : Math.max(0, cooridnates.top - menuHeight));
  };

  const isCompletedStatus = (): boolean => {
    return pollStatus === PollsViewStatuses.COMPLETED;
  };

  const isInProgressStatus = (): boolean => {
    return pollStatus === PollsViewStatuses.IN_PROGRESS;
  };

  // Next-Gen Player only: while THIS poll's results are shared with viewers, its own ellipsis menu
  // locks down entirely (it's the "active" poll — nothing here should be actionable).
  const isFullyLocked = !!isActivePollSharingResults;

  const isReopenDisabled = (): boolean => {
    return !isCompletedStatus() || isOnePollOpened || !!isResultsSharingLockedByOtherPoll;
  };

  const menuItemsProps = [
    { isDisabled: isReopenDisabled() || isFullyLocked, text: EllipsisMenu.REOPEN, icon: pollIcon, actionHandler: reopenPollActionHandler },
    { isDisabled: isInProgressStatus() || isFullyLocked, text: EllipsisMenu.EDIT, icon: pencilIcon, actionHandler: editActionHandler },
    { isDisabled: !isCompletedStatus() || isFullyLocked, text: EllipsisMenu.RESET, icon: circleArrowIcon, actionHandler: resetActionHandler },
    { isDisabled: isInProgressStatus() || isFullyLocked, text: EllipsisMenu.DELETE, icon: closeIcon, actionHandler: deleteActionHandler },
  ];

  // Re-measure before the first paint: the menu height is only known once it is in the DOM, so a menu that
  // has to flip is repositioned without ever being painted in the wrong place.
  useLayoutEffect(() => {
    setCoordinates();
  }, []);

  //Should use resize listener + fix position for menu, cos list have overflow for y axis
  useEffect(() => {
    window.addEventListener('resize', setCoordinates);

    return () => {
      window.removeEventListener('resize', setCoordinates);
    };
  }, []);


  return (
    <div className='poll-ellipsis-menu' ref={menuRef} style={{ left: leftCoordinates, top: topCoordinates }}>
      {menuItemsProps.map((el) => {
        return (
          <PollEllipsisMenuItem
            key={el.text}
            isDisabled={el.isDisabled}
            text={el.text}
            icon={el.icon}
            actionHandler={el.actionHandler}
            setShouldShowEllipsisMenu={setShouldShowEllipsisMenu}
          />
        );
      })
      }
    </div>
  );
};

export default PollEllipsisMenu;
